import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { FC, useEffect, useRef, useState } from "react";
import EventEntryForm, { EventEntryFormMode } from "../event-input-form";
import { EventEntryInput } from "../../../reducers/states/event-management";
import {
  createEventPayload,
  createEventRequest,
  createEventFailureAcknowledged,
} from "../../../actions/event-management";
import { useDispatch, useSelector } from "react-redux";
import { useAuthContext } from "../../../core";
import { UTCNowUtil } from "../../../utils/date-utils";
import { IRootReducer } from "../../../reducers";
import { RoleAction } from "../../../enums/permission-actions";
import useUserPermissions from "../../../hooks/user-permissions";

const EventCreationModal: FC = () => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("md"));
  const [isOpen, setIsOpen] = useState(false);
  const [isCreateFailedDialogOpen, setIsCreateFailedConfOpen] = useState(false);
  const [isSubmitBtnDisabled, setIsSubmitBtnDisabled] = useState(false);
  const [isCreateDisabled] = useState(false); // todo: useUserPermissions(RoleAction.CREATE_EVENT);
  const [eventInput, setEventInput] = useState<EventEntryInput | undefined>(undefined);
  const eventInputReceived = useRef<EventEntryInput>();
  const eventCreationStatus = useSelector((state: IRootReducer) => state.eventManagementReducer.eventCreationStatus);
  const [isCreateEventDisabled] = useUserPermissions(RoleAction.CREATE_MAJOR_EVENTS);

  const dispatch = useDispatch();
  const { authState } = useAuthContext();

  const handleOpen = () => {
    setIsOpen(true);
  };

  const handleCancel = (event?: object, reason?: string) => {
    if (reason === "backdropClick") return false;
    setIsOpen(false);
    resetForm();
  };

  const resetForm = () => {
    setEventInput(undefined);
    setIsSubmitBtnDisabled(false);
  };

  const handleEventInputChanged = (eventInfo: EventEntryInput): void => {
    // console.log("callback called - creation modal handleEventInputChanged", eventInfo);  // todo: jcavner: using for debug. delete eventually.
    eventInputReceived.current = eventInfo;
  };

  const handleCreate = () => {
    setIsSubmitBtnDisabled(true);
    const newEvent = {
      ...eventInputReceived.current,
      createdBy: authState.email ?? "",
      createdDate: UTCNowUtil().toISOString(),
    } as EventEntryInput;

    dispatch(createEventRequest(newEvent as createEventPayload));
  };

  useEffect(() => {
    if (eventCreationStatus === "Success") {
      setIsOpen(false);
      resetForm();
    } else if (eventCreationStatus === "Failed") {
      setIsCreateFailedConfOpen(true);
    } else if (!eventCreationStatus) {
      setIsCreateFailedConfOpen(false);
      setIsSubmitBtnDisabled(false);
    }
  }, [eventCreationStatus]);

  const displayCreateFailed = () => {
    return (
      <Dialog open={isCreateFailedDialogOpen} fullWidth maxWidth="md" aria-labelledby="form-create-failed-dialog-title">
        <DialogTitle>Creation Failed</DialogTitle>
        <DialogContent>
          An error occurred. You can try again now or later, or contact the administrator if you need assistance with
          this issue.
        </DialogContent>
        <DialogActions>
          <>
            <Button
              color="primary"
              variant="contained"
              onClick={() => {
                dispatch(createEventFailureAcknowledged());
              }}
            >
              Ok
            </Button>
          </>
        </DialogActions>
      </Dialog>
    );
  };

  return !isCreateEventDisabled ? (
    <>
      <Button
        variant="contained"
        size="medium"
        onClick={handleOpen}
        color="primary"
        style={{ alignSelf: "center" }}
        disabled={isCreateDisabled}
      >
        Create Event
      </Button>
      <Dialog
        fullScreen={fullScreen}
        open={isOpen}
        onClose={handleCancel}
        maxWidth="xl"
        aria-labelledby="form-dialog-title"
      >
        <DialogTitle id="form-dialog-title">Create Event</DialogTitle>
        <DialogContent>
          <EventEntryForm
            props={{
              mode: EventEntryFormMode.CREATE,
              event: eventInput,
              onEventInputChanged: handleEventInputChanged,
            }}
          />
          {displayCreateFailed()}
        </DialogContent>
        <DialogActions>
          <Button data-rum-id="create-event-cancel" onClick={handleCancel} color="inherit" variant="contained">
            Cancel
          </Button>
          <Button
            data-rum-id="create-event-confirm"
            onClick={handleCreate}
            color="primary"
            variant="contained"
            disabled={isSubmitBtnDisabled}
          >
            Create Event
          </Button>
        </DialogActions>
      </Dialog>
    </>
  ) : (<></>);
};

export default EventCreationModal;
